import { useMemo } from 'react'

import EmojiSelect from '@hotmart/space-lib/dist/components/emojiSelect'
import { toggleCommunityConfigurationSidebarEventDispatch } from '@space-lib-events/toggleCommunityConfigurationSidebar'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/lib/components'
import { CustomInput } from 'src/lib/components/customInput'
import { CustomSelect } from 'src/lib/components/customSelect'
import { CustomTextArea } from 'src/lib/components/customTextArea'

import { useGetGroups } from 'src/modules/community/hooks/useGetGroups'

import type { EditCommunityProps } from 'src/modules/community/types'
import { LimitPublicationsSwitch } from 'src/modules/settings/components/limitPublicationsSwitch'

import styles from './generalConfigPage.module.css'

import { Divider } from './styles'

import type { SelectOption } from './types'

import { useEditCommunity } from '../../../community/hooks/useEditCommunity'

import { useSettings } from '../../hooks/useSettings'
import { BlockCommentsSwitch } from '../blockCommentsSwitch'
import { COMMUNITY_NAME_MAX_LENGTH, DESCRIPTION_MAX_LENGTH } from '../constants'
import { HideCommunitySwitch } from '../hideCommunitySwitch'

const GeneralConfigPage = () => {
  const { t } = useTranslation()
  const {
    membershipSlug,
    resetForm,
    setIcon,
    setCommunityName,
    setGroupId,
    setDescription,
    community,
    communityName,
    description,
    isDataValid,
    groupId,
    icon,
    isCollaborativeContent,
    onSuccessEditCommunity,
    onErrorEditCommunity,
    isDefaultEnableComments,
    isHide,
    settingsFlags
  } = useSettings()
  const { groupCommunitiesList } = useGetGroups({ membershipSlug })
  const { editCommunityQuery } = useEditCommunity({ membershipSlug })

  const handleCancelButtonClick = () => {
    toggleCommunityConfigurationSidebarEventDispatch({ action: 'close' })

    resetForm()
  }

  const handleSubmit = () => {
    const payload = {
      name: communityName.trim(),
      description: description.trim(),
      icon: icon,
      groupId,
      id: community?.id,
      type: community?.type,
      collaborativeContent: isCollaborativeContent,
      defaultEnableComments: isDefaultEnableComments,
      hide: isHide
    } as EditCommunityProps

    editCommunityQuery.mutate(payload, {
      onSuccess: () => onSuccessEditCommunity(payload),
      onError: onErrorEditCommunity
    })
  }

  const groupOptions = useMemo(() => {
    return (
      groupCommunitiesList?.reduce((result: SelectOption[], item) => {
        result.push({ label: item.name, value: item.id })

        return result
      }, []) ?? []
    )
  }, [groupCommunitiesList])

  const handleEmojiSelect = (payload: string) => {
    setIcon(payload)
  }

  const handleChangeCommunityName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommunityName(event.target.value)
  }

  const handleChangeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value)
  }

  const handleSelectGroup = (event: Event) => {
    const groupId = (event as CustomEvent).detail.value

    setGroupId(Number(groupId))
  }

  return (
    <div className='_d-flex _flex-column'>
      <div className={styles.container}>
        <h2 className={styles.title}>{t('general', { ns: 'settings' })}</h2>
        <h3 className={styles.subtitle}>{t('presentation', { ns: 'settings' })}</h3>
        <p className={styles.description}>{t('description', { ns: 'settings' })}</p>
        <div className='_d-flex _align-items-start _mt-5'>
          <EmojiSelect
            name='icon'
            value={icon}
            onChange={handleEmojiSelect}
            label={t('icon', { ns: 'general' })}
            labelClassName='_text-gray-900'
          />

          <CustomInput
            value={communityName}
            onChange={handleChangeCommunityName}
            label={t('community_name', { ns: 'settings' })}
            name='community-name'
            className='_w-full _mb-0 _text-gray-900'
            placeholder={t('community_name_placeholder', { ns: 'settings' })}
            maxSize={COMMUNITY_NAME_MAX_LENGTH}
            currentSize={communityName?.length ?? 0}
          />
        </div>
        <CustomTextArea
          label={t('description_label', { ns: 'settings' })}
          name='description'
          value={description}
          onChange={handleChangeDescription}
          placeholder={t('description_placeholder', { ns: 'settings' })}
          maxSize={DESCRIPTION_MAX_LENGTH}
          currentSize={description?.length ?? 0}
          className='_mt-5 _mb-0 _text-gray-900'
        />

        <Divider />

        <h3 className={clsx(styles.subtitle, '_mt-5')}>{t('group.title', { ns: 'settings' })}</h3>
        <p className={styles.description}>{t('group.description', { ns: 'settings' })}</p>

        <CustomSelect
          name='group'
          onChange={handleSelectGroup}
          placeholder={t('group.placeholder', { ns: 'settings' })}
          items={groupOptions}
          selected={groupId}
          className='_mt-5 _mb-0 _text-gray-900'
        />

        <p className={styles.hint}>{t('group.hint', { ns: 'settings' })}</p>

        <Divider />

        <h3 className={clsx(styles.subtitle, '_mt-5')}>{t('permissions', { ns: 'settings' })}</h3>
        <p className={clsx(styles.description, '_mb-5')}>
          {t('permissions_description', { ns: 'settings' })}
        </p>

        <LimitPublicationsSwitch />

        <BlockCommentsSwitch />

        {settingsFlags?.enableHideCommunity && <HideCommunitySwitch />}
      </div>
      <footer className={styles.footer}>
        <Button variation='tertiary' onClick={handleCancelButtonClick}>
          {t('cancel', { ns: 'buttons' })}
        </Button>
        <Button
          variation='primary'
          onClick={handleSubmit}
          disabled={!isDataValid || editCommunityQuery.isLoading}
          loading={editCommunityQuery.isLoading}>
          {t('save', { ns: 'buttons' })}
        </Button>
      </footer>
    </div>
  )
}

export default GeneralConfigPage
